import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { Button, Table, View } from 'src/components/common';
import './styles.scss';
import { IRootState } from 'src/redux/rootReducer';
import { getUsersAsync, setOpenUserModal, setRefreshTable } from 'src/redux/users/actions';
import { UserInfo, UserModalType, GetUsersParams } from 'src/redux/users/types';
import { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import { useState } from 'react';
import UsersModal, { ModalTypes } from './UserModal';
import { useComponentWillUnmount } from 'src/hooks';
import _ from 'lodash';
import { getStatusDisplay, LockupValueByLabel } from 'src/utils';
import { isEmpty } from 'src/validations';

const INITIAL_USER: UserInfo = {
  id: '',
  lastName: '',
  firstName: '',
  email: '',
  roleId: -1,
  role: '',
  status: null,
  isActive: null,
};

const UsersContainer: React.FC<Props> = ({
  loading,
  users,
  totalUsers,
  openModalType,
  refreshTable,
  status,
  roles,
  onGetUsers,
  onSetOpenModal,
  onSetRefreshTable,
}) => {
  const [refresh, doRefresh] = useState<number>(0);
  useComponentWillUnmount(() => onSetOpenModal(null as UserModalType));

  useEffect(() => {
    if (refreshTable) {
      doRefreshTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTable]);
  const tableColumns: MUIDataTableColumn[] = [
    { name: 'lastName', label: 'Last Name', options: { filter: false, sort: true } },
    { name: 'firstName', label: 'First Name', options: { filter: false, sort: true } },
    { name: 'email', label: 'Email', options: { filter: false, sort: true } },
    {
      name: 'role',
      label: 'User Role',
      options: {
        filter: true,
        sort: true,
        filterOptions: {
          names: roles.map(item => item.label),
        },
        customBodyRender: (value: string) => {
          return _.capitalize(value);
        },
      },
    },
    {
      name: 'isActive',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        filterOptions: {
          names: status.map(item => _.capitalize(item.value)),
        },
        customBodyRender: (value: boolean) => (
          <span className={cn({ 'active-status': value, 'inactive-status': !value })}>{getStatusDisplay(value)}</span>
        ),
      },
    },
  ];

  const [selectedUser, setSelectedUser] = useState<UserInfo>(INITIAL_USER);

  const filterUsersParams = (params: GetUsersParams) => {
    let res = { ...params };
    Object.keys(res).forEach(key => {
      if (isEmpty(res[key])) delete res[key];
      if (key === 'role') {
        res['roleId'] = LockupValueByLabel(res.role[0] as string, roles);
        delete res.role;
      }
      if (key === 'isActive') {
        res['isActive'] = `${res.isActive[0] === 'Active'}`;
        // delete res.isActive;
      }
    });
    return res;
  };

  const handleGetUsers = (params: GetUsersParams) => {
    onGetUsers(filterUsersParams(params));
  };

  const handleRowClick = (_value: any, meta: { rowIndex: number }) => {
    const index = meta.rowIndex;
    setSelectedUser(users[index]);
    onSetOpenModal(ModalTypes.VIEW as UserModalType);
  };

  const handleCloseModal = () => {
    onSetOpenModal(false as UserModalType);
    setSelectedUser(INITIAL_USER);
  };

  const doRefreshTable = () => {
    doRefresh(prev => prev + 1);
    onSetRefreshTable(false);
  };

  const tableOptions: MUIDataTableOptions = {
    count: totalUsers,
    onRowClick: handleRowClick,
    searchPlaceholder: 'Search by user name or email',
  };

  return (
    <View className="ctn-users mt-32">
      <View className="mb-32" isRow justify="space-between">
        <View>
          <h3>Manage Users</h3>
        </View>
        <View>
          <Button
            onClick={() => {
              onSetOpenModal(ModalTypes.ADD as UserModalType);
              setSelectedUser(INITIAL_USER);
            }}>
            ADD NEW USER
          </Button>
        </View>
      </View>

      <Table
        title=""
        data={users}
        tableOptions={tableOptions}
        isLoading={loading}
        defaultSortOrder={{ name: 'email', direction: 'asc' }}
        columns={tableColumns}
        onAction={handleGetUsers}
        refresh={refresh}
      />
      <UsersModal
        data={selectedUser}
        openModalType={openModalType}
        onClose={handleCloseModal}
        onEdit={() => onSetOpenModal(ModalTypes.EDIT as UserModalType)}
      />
    </View>
  );
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const mapStateToProps = (state: IRootState) => ({
  loading: state.users.isLoading,
  users: state.users.users,
  totalUsers: state.users.totalUsers,
  openModalType: state.users.openUserModal,
  refreshTable: state.users.isRefreshTable,
  status: state.content.status,
  roles: state.content.roles,
});

const mapDispatchToProps = {
  onGetUsers: getUsersAsync.request,
  onSetOpenModal: setOpenUserModal,
  onSetRefreshTable: setRefreshTable,
};

export default connect(mapStateToProps, mapDispatchToProps)(UsersContainer);
